import { useId, type ReactNode } from "react";

import { useDismissOnEscape } from "../hooks/useDismissOnEscape";
import { useT } from "../i18n/LanguageProvider";

interface ModalProps {
  title: string;
  children: ReactNode;
  onClose: () => void;
}

/**
 * G-106 made this survive being stacked. Two things assumed one dialog:
 *  - the title's id was the literal "modal-title", so two open dialogs meant a
 *    duplicate DOM id and `aria-labelledby` resolving to whichever came first
 *    in the document — the one underneath. `useId` gives each instance its own.
 *  - the Escape listener fired for every mounted Modal at once; it now goes
 *    through `useDismissOnEscape`, which only lets the topmost overlay act.
 */
export function Modal({ title, children, onClose }: ModalProps) {
  const { t } = useT();
  const titleId = useId();

  useDismissOnEscape(onClose);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId} className="modal-title">
            {title}
          </h2>
          <button type="button" className="button plain" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
